import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import ErrorMessage from 'src/shared/components/error-msg/ErrorMessage';
import Alert from 'src/shared/components/ui/alert';
import useBoolean from 'src/shared/hooks/useBoolean';
import {
    useHasActiveEdnaDataReuploadRequestQuery,
    useGetLastEdnaDataReuploadQuery,
    useRequestEdnaDataReuploadReviewMutation,
} from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import { useProjectFiltersQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import EdnaDataReuploadReviewModal from './EdnaDataReuploadReviewModal';

const NEEDS_APPROVAL = 'NEEDS_APPROVAL';
const REVIEW_FAILED = 'REVIEW_FAILED';
const NM_PROVIDER = 'NatureMetrics';
const POLLING_INTERVAL_MS = 30_000;
const SUCCESS_DISMISS_MS = 5_000;

const isInProgress = (status?: string) => !!status && status !== NEEDS_APPROVAL && status !== REVIEW_FAILED;

interface ProjectDataRefreshProps {
    projectId: string;
}

const ProjectDataRefresh = ({ projectId }: ProjectDataRefreshProps) => {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isReviewModalOpen, reviewModal] = useBoolean(false);
    const [showInitiateSuccess, setShowInitiateSuccess] = useState(false);
    const [pollingInterval, setPollingInterval] = useState(0);

    const { data: activeRequest, isLoading, refetch } = useHasActiveEdnaDataReuploadRequestQuery({ projectId }, { pollingInterval });

    useEffect(() => {
        setPollingInterval(isInProgress(activeRequest?.status) ? POLLING_INTERVAL_MS : 0);
    }, [activeRequest?.status]);

    useEffect(() => {
        if (!showInitiateSuccess) return;
        const timer = setTimeout(() => setShowInitiateSuccess(false), SUCCESS_DISMISS_MS);
        return () => clearTimeout(timer);
    }, [showInitiateSuccess]);

    const { data: lastReupload } = useGetLastEdnaDataReuploadQuery({ projectId });
    const { data: filtersResponse, isLoading: isFiltersLoading } = useProjectFiltersQuery({ projectId });
    const [requestEdnaDataReuploadReview, { isLoading: isSubmitting }] = useRequestEdnaDataReuploadReviewMutation();

    if (isLoading || isFiltersLoading) {
        return null;
    }

    const hasThirdPartyData = filtersResponse?.filters?.map?.habitatAssays?.some(ha => ha.provider !== NM_PROVIDER) ?? false;

    const status = activeRequest?.status;

    if ((status === NEEDS_APPROVAL || status === REVIEW_FAILED) && activeRequest?.data) {
        const { requestId, requestor, createdAt, reviewData, errorDetails } = activeRequest.data;
        return (
            <>
                {status === REVIEW_FAILED ? (
                    <SecondaryButton onClick={reviewModal.on} className='text-red-600 border-red-400'>
                        Review Failed
                    </SecondaryButton>
                ) : (
                    <PrimaryButton onClick={reviewModal.on}>Review</PrimaryButton>
                )}
                {isReviewModalOpen && (
                    <EdnaDataReuploadReviewModal
                        requestId={requestId}
                        requestor={requestor ?? ''}
                        createdAt={createdAt}
                        status={status}
                        errorDetails={errorDetails ?? null}
                        reviewData={reviewData ?? []}
                        onClose={reviewModal.off}
                        onActionComplete={refetch}
                    />
                )}
            </>
        );
    }

    // Any other active status — review data is being prepared
    if (activeRequest) {
        return (
            <div className='flex flex-col gap-2'>
                {showInitiateSuccess && <Alert severity='success'>eDNA Data Reupload request initiated successfully.</Alert>}
                <p className='text-sm font-medium text-grey-80'>
                    Review data is being prepared. Once ready, you will be asked to review and approve the reupload before it proceeds.
                </p>
            </div>
        );
    }

    // No active request — show submit button (and last upload date if available)
    const handleClick = async () => {
        setErrorMessage(null);
        try {
            await requestEdnaDataReuploadReview({ projectId }).unwrap();
            setShowInitiateSuccess(true);
            refetch();
        } catch {
            setErrorMessage('Failed to submit eDNA data reupload request.');
        }
    };

    const lastUploadedLabel = lastReupload?.approvedAt ? moment(lastReupload.approvedAt).format('DD MMM YYYY HH:mm') : 'Never';

    return (
        <div>
            <p className='text-sm text-grey-60 mb-2'>
                Last eDNA data upload: <strong>{lastUploadedLabel}</strong>
            </p>
            {errorMessage && <ErrorMessage message={errorMessage} />}
            {hasThirdPartyData && <p className='text-sm text-grey-60 mb-2'>Not available — this project contains third-party data.</p>}
            <PrimaryButton onClick={handleClick} disabled={isSubmitting || hasThirdPartyData}>
                {isSubmitting ? 'Initiating...' : 'Initiate eDNA Data Reupload'}
            </PrimaryButton>
        </div>
    );
};

export default ProjectDataRefresh;
